import React, { useState, useEffect } from 'react';
import { RosaryIcon } from '../../icons/RosaryIcon';

interface FeaturePreviewScreenProps {
  onContinue: () => void;
}

interface Feature {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  visual: React.ReactNode;
}

const features: Feature[] = [
  {
    id: '54-day-novena',
    title: '54-Day Rosary Novena',
    description: 'The most powerful novena - 27 days of petition followed by 27 days of thanksgiving.',
    icon: <RosaryIcon size={64} className="text-indigo-600 dark:text-indigo-400" />,
    visual: (
      <div className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow-xl max-w-xs mx-auto">
        <div className="space-y-4">
          {/* Calendar visual */}
          <div className="text-center">
            <div className="text-5xl font-bold text-indigo-600 dark:text-indigo-400">Day 1</div>
            <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">Petition Phase</div>
          </div>
          {/* Progress indicator */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-600 dark:text-gray-300">
              <span>Petition</span>
              <span>1/27 days</span>
            </div>
            <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-full w-[4%]" />
            </div>
          </div>
          {/* Intention display */}
          <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3 text-center">
            <p className="text-sm text-gray-700 dark:text-gray-300 italic">
              "Your intention guides your 54-day journey"
            </p>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'virtual-rosary',
    title: 'Virtual Rosary',
    description: 'Beautiful interactive rosary that keeps count so you can focus on prayer.',
    icon: <RosaryIcon size={64} className="text-indigo-600 dark:text-indigo-400" />,
    visual: (
      <div className="relative w-48 h-48 mx-auto">
        {/* Simplified rosary visualization */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            {/* Center cross */}
            <div className="w-12 h-16 bg-gradient-to-b from-indigo-600 to-indigo-700 rounded-sm mx-auto mb-4 shadow-lg" />
            {/* Beads circle */}
            <div className="relative w-32 h-32 mx-auto">
              {[...Array(10)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-4 h-4 bg-indigo-500 rounded-full shadow-lg"
                  style={{
                    top: `${50 + 40 * Math.sin((i * Math.PI * 2) / 10)}%`,
                    left: `${50 + 40 * Math.cos((i * Math.PI * 2) / 10)}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              ))}
              {/* Center indicator */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full shadow-xl animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'journal',
    title: 'Prayer Journal',
    description: 'Capture intentions before prayer. Reflect on graces received after.',
    icon: '📔',
    visual: (
      <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-xl max-w-xs mx-auto">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-gray-900 dark:text-white">
            <span className="text-2xl">😊</span>
            <span className="font-semibold">Feeling peaceful today</span>
          </div>
          <div className="h-20 bg-gray-100 dark:bg-gray-600 rounded-lg p-3">
            <p className="text-sm text-gray-600 dark:text-gray-300 italic">
              "Grateful for this moment of prayer. Asking for strength for my family..."
            </p>
          </div>
          <div className="flex gap-2">
            {['peaceful', 'grateful', 'hopeful'].map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-1 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'progress',
    title: 'Progress Tracking',
    description: 'See your spiritual journey unfold. Track consistency and growth.',
    icon: '📊',
    visual: (
      <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-xl max-w-xs mx-auto">
        <div className="space-y-4">
          {/* Progress stat */}
          <div className="text-center">
            <div className="text-4xl font-bold text-indigo-600 dark:text-indigo-400">7</div>
            <div className="text-sm text-gray-600 dark:text-gray-300">Day streak</div>
          </div>
          {/* Progress bars */}
          <div className="space-y-2">
            <div>
              <div className="flex justify-between text-xs text-gray-600 dark:text-gray-300 mb-1">
                <span>Week 1</span>
                <span>5/7 days</span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-full w-[71%]" />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs text-gray-600 dark:text-gray-300 mb-1">
                <span>Total prayers</span>
                <span>12</span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-600 to-emerald-600 rounded-full w-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  },
];

export const FeaturePreviewScreen: React.FC<FeaturePreviewScreenProps> = ({ onContinue }) => {
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const currentFeature = features[currentFeatureIndex];
  const isLastFeature = currentFeatureIndex === features.length - 1;

  // Auto-advance timer (10 seconds per feature)
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + (100 / 100); // 100 steps = 10 seconds
        if (newProgress >= 100 && !isLastFeature) {
          setCurrentFeatureIndex((i) => i + 1);
          return 0;
        }
        return newProgress >= 100 ? 100 : newProgress;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [currentFeatureIndex, isLastFeature]);

  const handleNext = () => {
    if (isLastFeature) {
      onContinue();
    } else {
      setCurrentFeatureIndex(currentFeatureIndex + 1);
      setProgress(0);
    }
  };

  const handleSkip = () => {
    onContinue();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Your complete prayer companion
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          Quick tour of what makes this app special
        </p>
      </div>

      {/* Feature display */}
      <div className="flex-1 flex flex-col items-center justify-center space-y-8">
        {/* Feature icon */}
        <div className="text-6xl animate-bounce">{currentFeature.icon}</div>

        {/* Feature visual */}
        <div className="w-full max-w-md">{currentFeature.visual}</div>

        {/* Feature info */}
        <div className="text-center max-w-lg">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {currentFeature.title}
          </h2>
          <p className="text-gray-600 dark:text-gray-300">{currentFeature.description}</p>
        </div>

        {/* Progress dots */}
        <div className="flex gap-2">
          {features.map((feature, index) => (
            <button
              key={feature.id}
              onClick={() => {
                setCurrentFeatureIndex(index);
                setProgress(0);
              }}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentFeatureIndex
                  ? 'w-8 bg-indigo-600'
                  : index < currentFeatureIndex
                  ? 'w-2 bg-indigo-400'
                  : 'w-2 bg-gray-300 dark:bg-gray-600'
              }`}
            />
          ))}
        </div>

        {/* Auto-progress bar */}
        <div className="w-full max-w-md">
          <div className="h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-indigo-600 to-indigo-700 transition-all duration-100"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="mt-8 space-y-3">
        <button
          onClick={handleNext}
          className="w-full py-4 rounded-xl font-semibold text-lg transition-all duration-200 transform bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white hover:scale-105 shadow-lg"
        >
          {isLastFeature ? "Let's Pray Together" : 'Next'}
        </button>
        <button
          onClick={handleSkip}
          className="w-full py-3 rounded-xl font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
        >
          Skip tour
        </button>
      </div>
    </div>
  );
};